import { interpolate } from "flubber";
import React, { RefObject } from "react";
import { Animated, Easing, Platform, StyleProp, ViewStyle } from "react-native";
import SVG_Svg, { G as SVG_G, Path as SVG_Path } from "react-native-svg";
/* SVGR has dropped some elements not supported by react-native-svg: desc */

const Path = Animated.createAnimatedComponent(SVG_Path);
const G = Animated.createAnimatedComponent(SVG_G);
const Svg = Animated.createAnimatedComponent(SVG_Svg);
/***/

/**
 *
 *  /$$$$$$                               /$$$$$$$  /$$
 * |_  $$_/                              | $$__  $$| $$
 *   | $$   /$$$$$$/$$$$   /$$$$$$       | $$  \ $$| $$ /$$   /$$  /$$$$$$
 *   | $$  | $$_  $$_  $$ /$$__  $$      | $$$$$$$ | $$| $$  | $$ /$$__  $$
 *   | $$  | $$ \ $$ \ $$| $$  \ $$      | $$__  $$| $$| $$  | $$| $$$$$$$$
 *   | $$  | $$ | $$ | $$| $$  | $$      | $$  \ $$| $$| $$  | $$| $$_____/
 *  /$$$$$$| $$ | $$ | $$| $$$$$$$/  __  | $$$$$$$/| $$|  $$$$$$/|  $$$$$$$
 * |______/|__/ |__/ |__/| $$____/  |$$| |_______/ |__/ \______/  \_______/
 *                       | $$
 *                       | $$
 *                       |__/
 *
 */

/*
 * ORIGINAL SVG - STATE 0
 * ---------------------------------
 * <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"> <desc id="IMP.BLUE">{"morph":{"condition":"props.morph","targets":{"state=off":{"type":"ease","duration":250,"delay":0},"state=on":{"type":"ease","duration":250,"delay":0}},"numStates":2},"tProps":{"type":"PROPS","morph?":"number","maxSegmentLength?":"number","state?":"\"off\" | \"on\"","onAnimationComplete?":"() => void","morph":"number","button?":{"onPress":"Function"}},"#button":{"type":"SELECTOR","touchableOpacity":{"onPress":"props?.button?.onPress"}}}</desc>
 * <g id="state=off">
 * <circle id="BG" cx="7.5" cy="7.5" r="7.5" fill="white"/>
 * <path id="Outer (Stroke)" fill-rule="evenodd" clip-rule="evenodd" d="M7.5 13C10.5376 13 13 10.5376 13 7.5C13 4.46243 10.5376 2 7.5 2C4.46243 2 2 4.46243 2 7.5C2 10.5376 4.46243 13 7.5 13ZM7.5 15C11.6421 15 15 11.6421 15 7.5C15 3.35786 11.6421 0 7.5 0C3.35786 0 0 3.35786 0 7.5C0 11.6421 3.35786 15 7.5 15Z" fill="black"/>
 * <circle id="Inner" opacity="0.01" cx="7.5" cy="7.5" r="0.5" fill="black"/>
 * <circle id="button" cx="7.5" cy="7.5" r="7.5" fill="white" fill-opacity="0.01"/>
 * </g>
 * </svg>
 *
 * ---------------------------------
 *
 * MORPH TARGET - STATE 1
 * ---------------------------------
 * <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
 * <g id="state=on">
 * <circle id="BG" cx="7.5" cy="7.5" r="7.5" fill="white"/>
 * <path id="Outer (Stroke)" fill-rule="evenodd" clip-rule="evenodd" d="M7.5 13C10.5376 13 13 10.5376 13 7.5C13 4.46243 10.5376 2 7.5 2C4.46243 2 2 4.46243 2 7.5C2 10.5376 4.46243 13 7.5 13ZM7.5 15C11.6421 15 15 11.6421 15 7.5C15 3.35786 11.6421 0 7.5 0C3.35786 0 0 3.35786 0 7.5C0 11.6421 3.35786 15 7.5 15Z" fill="black"/>
 * <circle id="Inner" cx="7.5" cy="7.5" r="4.5" fill="black"/>
 * <circle id="button" cx="7.5" cy="7.5" r="7.5" fill="white" fill-opacity="0.01"/>
 * </g>
 * </svg>
 *
 * ---------------------------------
 *
 * CONFIGURATION
 * ---------------------------------
 * {
 * 	"morph": {
 * 		"condition": "props.morph",
 * 		"targets": {
 * 			"state=off": {
 * 				"type": "ease",
 * 				"duration": 250,
 * 				"delay": 0
 * 			},
 * 			"state=on": {
 * 				"type": "ease",
 * 				"duration": 250,
 * 				"delay": 0
 * 			}
 * 		},
 * 		"numStates": 2
 * 	},
 * 	"tProps": {
 * 		"type": "PROPS",
 * 		"morph?": "number",
 * 		"maxSegmentLength?": "number",
 * 		"state?": "\"off\" | \"on\"",
 * 		"onAnimationComplete?": "() => void",
 * 		"morph": "number",
 * 		"button?": {
 * 			"onPress": "Function"
 * 		}
 * 	},
 * 	"#button": {
 * 		"type": "SELECTOR",
 * 		"touchableOpacity": {
 * 			"onPress": "props?.button?.onPress",
 * 			"animation": "touchableOpacity"
 * 		}
 * 	}
 * }
 * ---------------------------------
 *
 * DESCRIPTION
 * ---------------------------------
 * No description provided!
 * ---------------------------------
 */

/** Component Properties Interface */
export type tProps = {
  style?: StyleProp<ViewStyle>;
  debug?: boolean;
  svgRef?: unknown;
  hasDelay?: boolean;
  morph?: number;
  maxSegmentLength?: number;
  state?: "off" | "on";
  onAnimationComplete?: () => void;
  button?: {
    onPress: () => void;
  };
};
/***/

/** Morphing Types */

type tMorphRef = Animated.WithAnimatedValue<React.Ref<React.Component<unknown, unknown, unknown>>> &
  React.Ref<React.Component<unknown, unknown, unknown>>;
type tPathRef = typeof Path & {
  // Native Control
  setNativeProps: ({ d }: { d: string }) => void; // Web Control

  _touchableNode: {
    setAttribute: (attr: string, val: string) => void;
  }; // Get Ids

  props: {
    id: string;
  };
};
type tMorphProps = {
  morphConfigInneropacity: string | Animated.AnimatedInterpolation;
  morphConfigInnerd: string | Animated.AnimatedInterpolation;
  bindInterpolation: tMorphRef;
};
/***/

/**
 * Auto-Generated Imp.Blue Component based on your design!
 * @param {tProps} props Developer interface to the component!
 * @returns {JSX.Element} JSX
 *
 * Description --
 * No description provided!
 */

export const Radio_Button = (props: {
  style?: StyleProp<ViewStyle>;
  debug?: boolean;
  svgRef?: unknown;
  hasDelay?: boolean;
  morph?: number;
  maxSegmentLength?: number;
  state?: "off" | "on";
  onAnimationComplete?: () => void;
  button?: {
    onPress: () => void;
  };
}): JSX.Element => {
  const { svgRef, maxSegmentLength = 1, onAnimationComplete = () => 0, button } = props;
  /** Component Internal State */

  const [buttonState, setButtonState] = React.useState<{
    buttonButtonOpacity: number;
  }>({
    buttonButtonOpacity: 1,
  });
  const [morphState] = React.useState<{
    morphAnimVal: Animated.Value;
    morphProps: tMorphProps;
  }>({
    morphAnimVal: new Animated.Value(0),
    morphProps: {
      bindInterpolation: () => undefined,
      morphConfigInneropacity: "0.01",
      morphConfigInnerd: "M8 7.5 A0.5 0.5 0 0 1 7.5 8 A0.5 0.5 0 0 1 7 7.5 A0.5 0.5 0 0 1 8 7.5z",
    },
  });
  /** End Custom React-Hooks */

  /** Button Wrapper */
  // #region button Button Handlers

  const buttonButtonPressInFunction = () => 0;

  const buttonButtonPressOutFunction = button?.onPress;

  const buttonButtonActive = (callback?: () => void) => {
    setButtonState({ ...buttonState, buttonButtonOpacity: 0.2 });

    if (callback) {
      callback();
    }
  };

  const buttonButtonInactive = (callback?: () => void) => {
    setButtonState({ ...buttonState, buttonButtonOpacity: 1 });

    if (callback) {
      callback();
    }
  };

  const buttonOnPressIn = () => {
    buttonButtonActive(buttonButtonPressInFunction);
  };

  const buttonOnPressOut = () => {
    buttonButtonInactive(buttonButtonPressOutFunction);
  };
  /***/
  // #endregion

  /** End Button Wrapper */

  /** Morph Wrapper */

  type tMorphProperty = {
    state: "off" | "on";
  };
  type tMorphProperties = {
    [key: string]: tMorphProperty;
  };
  const morphStates: tMorphProperties = {
    0: {
      state: "off",
    },
    1: {
      state: "on",
    },
  };

  function getNextMorphState(): number {
    const { state, morph = 0 } = props;
    let nextState = morph;
    Object.keys(morphStates).forEach((possibleMorphState) => {
      if (morphStates[possibleMorphState].state === state) {
        nextState = parseInt(possibleMorphState, 10);
      }
    });
    return nextState;
  }

  const { morphAnimVal, morphProps } = morphState;

  const currValueRef = React.useRef(0);
  const nextValue: number = getNextMorphState() ?? 1;
  const currValue = currValueRef.current;
  let startValue = Math.floor(currValue);
  let endValue = Math.ceil(nextValue);

  if (currValue > nextValue) {
    startValue = Math.ceil(currValue);
    endValue = Math.floor(nextValue);
  }
  /**
   * Normalize an input to a range of 0 to 1
   */

  const normalize = (x: number, bound1: number, bound2: number) =>
    Number((x - Math.min(bound1, bound2)) / (Math.max(bound1, bound2) - Math.min(bound1, bound2)));
  /**
   * Custom Hook to Control Animation Interpolation of SVG Paths
   */

  const useGenerateMorphInterpolation = (
    interpolations: Array<{
      morphId: keyof tMorphProps;
      paths: string[];
    }>,
    start: number,
    end: number,
  ) => {
    const refs = React.useRef<Record<string, tPathRef>>({});
    React.useEffect(() => {
      const interpolators: {
        [morphId: string]: {
          initial: string;
          final: string;
          interpolator: (t: number) => string;
        };
      } = {};
      interpolations.forEach(({ morphId, paths }) => {
        const initial = paths[start];
        const final = paths[end];
        const p1 = start > end ? final : initial;
        const p2 = start > end ? initial : final;
        const interpolator = interpolate(p1, p2, {
          maxSegmentLength,
        });
        interpolators[morphId] = {
          initial,
          final,
          interpolator,
        };
      });

      const onValueChange = ({ value }: { value: number }) => {
        Object.keys(interpolators).forEach((morphId) => {
          const { final, interpolator } = interpolators[morphId];
          let nextInterpolatedPath: string;

          if (value % 1 === 0) {
            nextInterpolatedPath = final;
          } else {
            nextInterpolatedPath = interpolator(normalize(value, start, end));
          }

          if (morphId in refs.current) {
            const thisRef = refs.current[morphId];

            if (Platform.OS === "web") {
              thisRef?._touchableNode?.setAttribute("d", nextInterpolatedPath);
            } else {
              thisRef?.setNativeProps({
                d: nextInterpolatedPath,
              });
            }
          }
        });
      };

      const listenerId = morphAnimVal.addListener(onValueChange);
      return () => morphAnimVal.removeListener(listenerId); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start, end]);
    return ((ref: tPathRef) => {
      const id = ref?.props?.id ?? "unknown";

      if (refs && refs.current) {
        refs.current[`morphConfig${id}d`] = ref;
      }
    }) as unknown as tMorphRef;
  };

  morphProps.bindInterpolation = useGenerateMorphInterpolation(
    [
      {
        morphId: "morphConfigInnerd",
        paths: [
          "M8 7.5 A0.5 0.5 0 0 1 7.5 8 A0.5 0.5 0 0 1 7 7.5 A0.5 0.5 0 0 1 8 7.5z",
          "M12 7.5 A4.5 4.5 0 0 1 7.5 12 A4.5 4.5 0 0 1 3 7.5 A4.5 4.5 0 0 1 12 7.5z",
        ],
      },
    ],
    startValue,
    endValue,
  );
  const morphValues: {
    [id: string]: Array<string>;
  } = {
    morphConfigInneropacity: ["0.01", "1"],
  };

  function clamp(lower: number, upper: number, v: number) {
    let r = v;

    if (r > upper) {
      r = upper;
    } else if (r < lower) {
      r = lower;
    }

    return r;
  }

  function getInterpolationRange(
    morphId: string,
    start: number,
    end: number,
  ): {
    inputRange: number[];
    outputRange: string[];
  } {
    const initial = clamp(0, 2, start);
    const final = clamp(0, 2, end);
    const inputRange = [start, end];
    const outputRange = [morphValues[morphId][initial], morphValues[morphId][final]];

    if (start > end) {
      inputRange.reverse();
      outputRange.reverse();
    }

    return {
      inputRange,
      outputRange,
    };
  }

  morphProps.morphConfigInneropacity = morphAnimVal.interpolate(
    getInterpolationRange("morphConfigInneropacity", startValue, endValue),
  );
  React.useEffect(() => {
    const forwardMorph = [
      Animated.timing(morphAnimVal, {
        easing: Easing.ease,
        toValue: nextValue,
        duration: 150,
        delay: props.hasDelay ? 0 : 0,
        useNativeDriver: false,
      }),
      Animated.timing(morphAnimVal, {
        easing: Easing.ease,
        toValue: nextValue,
        duration: 150,
        delay: props.hasDelay ? 0 : 0,
        useNativeDriver: false,
      }),
    ];

    const restartAnimation = () => {
      const nextInt = Math.ceil(nextValue);

      if (nextInt in forwardMorph) {
        forwardMorph[nextInt]?.start(() => {
          currValueRef.current = nextInt;
          onAnimationComplete();
        });
      }
    };

    if (morphAnimVal && morphAnimVal.stopAnimation) {
      morphAnimVal.stopAnimation(restartAnimation);
    } else {
      restartAnimation();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextValue]);
  /** End Morph Wrapper */

  /** Component Render (Javascript-React Markup) */

  return (
    <Svg viewBox="0 0 15 15" fill="none" id="svg_root" ref={svgRef as RefObject<unknown>} {...props}>
      <G id="state_3Doff">
        <Path
          id="BG"
          fill="white"
          d="M15 7.5 A7.5 7.5 0 0 1 7.5 15 A7.5 7.5 0 0 1 0 7.5 A7.5 7.5 0 0 1 15 7.5z"
          ref={morphProps.bindInterpolation}
          strokeWidth="1"
        />
        <Path
          id="Outer_20_28Stroke_29"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.5 13C10.5376 13 13 10.5376 13 7.5C13 4.46243 10.5376 2 7.5 2C4.46243 2 2 4.46243 2 7.5C2 10.5376 4.46243 13 7.5 13ZM7.5 15C11.6421 15 15 11.6421 15 7.5C15 3.35786 11.6421 0 7.5 0C3.35786 0 0 3.35786 0 7.5C0 11.6421 3.35786 15 7.5 15Z"
          fill="black"
          ref={morphProps.bindInterpolation}
          strokeWidth="1"
        />
        <Path
          id="Inner"
          fill="black"
          d="M8 7.5 A0.5 0.5 0 0 1 7.5 8 A0.5 0.5 0 0 1 7 7.5 A0.5 0.5 0 0 1 8 7.5z"
          ref={morphProps.bindInterpolation}
          strokeWidth="1"
          opacity={morphProps.morphConfigInneropacity}
        />
        <Path
          id="button"
          fill="white"
          fillOpacity={0.01}
          d="M15 7.5 A7.5 7.5 0 0 1 7.5 15 A7.5 7.5 0 0 1 0 7.5 A7.5 7.5 0 0 1 15 7.5z"
          ref={morphProps.bindInterpolation}
          strokeWidth="1"
          opacity={buttonState.buttonButtonOpacity}
          onPressIn={buttonOnPressIn}
          onPressOut={buttonOnPressOut}
          onMouseDown={buttonOnPressIn}
          onMouseUp={buttonOnPressOut}
          cursor="pointer"
        />
      </G>
    </Svg>
  );
};
/***/

export default Radio_Button;
